// src/services/azureSitzungFolienService.js
// Azure Table Storage + Blob Storage Service für die optionale PDF-Präsentation
// im Sitzungsmodus (US-SITZ-003, ADR 0059).
//
// Eine Präsentation gehört zu genau einer Sitzung (PartitionKey = sitzungId) und
// wird beim Hochladen clientseitig in Einzelseiten ("Folien") zerlegt und als
// Mid-Resolution-JPEG im Blob Storage abgelegt – Muster für PDF->Canvas->Blob
// aus azurePlansService.js/PlanViewer.jsx (pdfjs), Muster für den Blob-Upload
// aus azurePlansService.js (Account-SAS, gleicher Token wie project-plans).
//
// Tabelle `sitzungFolien`, zwei Zeilenarten je Partition (sitzungId):
//  - RowKey "meta"        : Dateiname, SeitenAnzahl, HochgeladenAm/Von der Präsentation
//  - RowKey "folie_0001".."folie_9999": eine Zeile pro Folie (Seite, BlobPath)
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { ensureTableExists } from "./azureTableUtils";
import { getPendenzen, updatePendenz } from "./azurePendenzenService";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const TABLE_NAME = "sitzungFolien";
const TABLE_CONFIG = {
  storageAccount: "mfprojects",
  // Gleicher Account-SAS-Token wie azurePendenzenService.js/azureSitzungenService.js
  // (Table-Service, kontoweit gültig, daher auch für neue Tabellen nutzbar).
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Eigener, nicht öffentlicher Blob-Container – Sitzungsfolien können vertrauliche
// Bauherren-/Planungsinhalte zeigen, daher wie project-plans NICHT öffentlich
// lesbar, sondern über den (bereits im Projekt verwendeten) Blob-SAS-Token.
const BLOB_CONFIG = {
  storageAccount: "mfprojects",
  containerName: "sitzung-folien",
  sasToken:
    "sv=2024-11-04&ss=b&srt=co&sp=rwdlaciytfx&se=2035-10-02T19:07:10Z&st=2025-10-02T10:52:10Z&spr=https&sig=A8jWH2s8LkqmqiQjwjR8bL4sLjDKgKlE1HkDCZSlsYk%3D",
};

const getBaseUrl = () =>
  `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_NAME}`;

const cleanValue = (value) => (value === null || value === undefined ? "" : String(value));

const padSeite = (n) => String(n).padStart(4, "0");
const folieRowKey = (seite) => `folie_${padSeite(seite)}`;

let containerEnsured = false;
const ensureContainerExists = async () => {
  if (containerEnsured) return true;
  try {
    const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}?restype=container&${BLOB_CONFIG.sasToken}`;
    const response = await fetch(url, { method: "PUT" });
    if (response.ok || response.status === 409) {
      containerEnsured = true;
    }
    return containerEnsured;
  } catch (error) {
    console.error("Sitzungsfolien: Container konnte nicht angelegt werden:", error);
    return false;
  }
};

// Öffentliche Lese-URL für eine Folie (Container ist nicht öffentlich, daher mit SAS).
export const buildFolieUrl = (blobPath) =>
  `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;

const uploadBlob = async (blobPath, blob) => {
  const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": "image/jpeg",
    },
    body: blob,
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Sitzungsfolie-Blob-Upload-Fehler:", errorText);
    throw new Error("Eine Folie konnte nicht hochgeladen werden.");
  }
};

const deleteBlob = async (blobPath) => {
  const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok && response.status !== 404) {
    console.error("Sitzungsfolie-Blob-Delete-Fehler:", await response.text());
  }
};

const genericLoad = async (filter) => {
  let allEntries = [];
  let nextPartitionKey = null;
  let nextRowKey = null;
  do {
    let url = `${getBaseUrl()}()?${TABLE_CONFIG.sasToken}&$filter=${encodeURIComponent(filter)}`;
    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error:", errorText);
      throw new Error(`Azure Table Error: ${response.status}`);
    }
    const data = await response.json();
    allEntries = allEntries.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);
  return allEntries;
};

const insertEntity = async (entity) => {
  const response = await fetch(`${getBaseUrl()}?${TABLE_CONFIG.sasToken}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Azure Save Error Details:", errorText);
    throw new Error(`Azure Save Error: ${response.status}`);
  }
};

const deleteEntity = async (partitionKey, rowKey) => {
  const url = `${getBaseUrl()}(PartitionKey='${partitionKey}',RowKey='${rowKey}')?${TABLE_CONFIG.sasToken}`;
  const response = await fetch(url, { method: "DELETE", headers: { "If-Match": "*" } });
  if (!response.ok && response.status !== 404) {
    console.error("Azure Delete Error:", await response.text());
  }
};

// Partielles Update (nur die genannten Felder) – hier für das Nachführen von
// SeitenAnzahl in der Meta-Zeile nach dem Bereinigen ungenutzter Folien.
const mergeEntity = async (partitionKey, rowKey, patch) => {
  const url = `${getBaseUrl()}(PartitionKey='${partitionKey}',RowKey='${rowKey}')?${TABLE_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "MERGE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
      "If-Match": "*",
    },
    body: JSON.stringify({ PartitionKey: partitionKey, RowKey: rowKey, ...patch }),
  });
  if (!response.ok) {
    console.error("Azure Merge Error:", await response.text());
  }
};

const mapMeta = (item) => ({
  dateiname: item.Dateiname || "",
  seitenAnzahl: parseInt(item.SeitenAnzahl, 10) || 0,
  hochgeladenAm: item.HochgeladenAm || "",
  hochgeladenVon: item.HochgeladenVon || "",
});

const mapFolie = (item) => ({
  id: item.RowKey,
  sitzungId: item.PartitionKey,
  seite: parseInt(item.Seite, 10) || 0,
  blobPath: item.BlobPath || "",
  url: buildFolieUrl(item.BlobPath || ""),
});

// Lädt die Präsentation (Meta + Folien, aufsteigend sortiert) einer Sitzung.
// null-Meta bedeutet: keine Präsentation hochgeladen.
export const getPraesentation = async (sitzungId) => {
  if (!sitzungId) return { meta: null, folien: [] };
  await ensureTableExists(TABLE_NAME);
  const entries = await genericLoad(`PartitionKey eq '${sitzungId}'`);
  const metaEntry = entries.find((e) => e.RowKey === "meta");
  const folien = entries
    .filter((e) => e.RowKey.startsWith("folie_"))
    .map(mapFolie)
    .sort((a, b) => a.seite - b.seite);
  return { meta: metaEntry ? mapMeta(metaEntry) : null, folien };
};

// Rasterisiert eine PDF-Seite auf ein Canvas (Mid-Resolution: Ziel ~1400px
// Breite) und liefert einen JPEG-Blob. Muster: PlanViewer.jsx (pdfjs render),
// azureBlobService.js resizeImage (canvas.toBlob).
const renderPageToJpegBlob = async (pdfDoc, pageNumber) => {
  const page = await pdfDoc.getPage(pageNumber);
  const unscaled = page.getViewport({ scale: 1 });
  const targetWidth = 1400;
  const scale = Math.min(3, Math.max(0.5, targetWidth / unscaled.width));
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Folie konnte nicht gerendert werden."))),
      "image/jpeg",
      0.82
    );
  });
};

// Entfernt eine bestehende Präsentation (Blobs + Tabellenzeilen) und löst bei
// betroffenen Pendenzen die Folien-Zuordnung (analog deleteSitzung/sitzungId).
export const deletePraesentation = async (projectUuid, sitzungId) => {
  if (!sitzungId) return;
  await ensureTableExists(TABLE_NAME);
  const { folien } = await getPraesentation(sitzungId);

  if (projectUuid && folien.length > 0) {
    const folieIds = new Set(folien.map((f) => f.id));
    const pendenzen = await getPendenzen(projectUuid);
    const betroffene = pendenzen.filter((p) => {
      const anchor = p.praesentationAnchor || "";
      if (!anchor) return false;
      const [kind, ref] = anchor.split(":");
      if (kind === "slide") return folieIds.has(ref);
      if (kind === "gap") return ref === "start" || folieIds.has(ref);
      return false;
    });
    for (const p of betroffene) {
      await updatePendenz(projectUuid, p.id, { ...p, praesentationAnchor: "" });
    }
  }

  for (const f of folien) {
    if (f.blobPath) await deleteBlob(f.blobPath);
    await deleteEntity(sitzungId, f.id);
  }
  await deleteEntity(sitzungId, "meta");
};

// Entfernt beim Verlassen des Sitzungsmodus alle Folien OHNE zugeordnete
// Pendenz (weder "neben der Folie" noch "im Zwischenraum danach") – anders
// als deletePraesentation bleibt die Zuordnung genutzter Folien (und damit
// ihr Bild) unangetastet erhalten, es werden nur die wirklich ungenutzten
// entfernt. Liest die Pendenzen FRISCH aus Azure (nicht aus React-State),
// damit gerade erst gespeicherte Zuordnungen sicher berücksichtigt werden.
// Bleibt keine einzige Folie übrig, ist die ganze Präsentation gegenstandslos
// und wird komplett entfernt (inkl. Meta-Zeile); werden nur einzelne Folien
// entfernt, wird SeitenAnzahl in der Meta-Zeile auf die verbleibende Anzahl
// nachgeführt.
export const pruneUnusedFolien = async (projectUuid, sitzungId) => {
  if (!sitzungId) return;
  const { folien } = await getPraesentation(sitzungId);
  if (folien.length === 0) return;

  const pendenzen = projectUuid ? await getPendenzen(projectUuid) : [];
  const usedFolieIds = new Set();
  pendenzen.forEach((p) => {
    const anchor = p.praesentationAnchor || "";
    if (!anchor) return;
    const [kind, ref] = anchor.split(":");
    if (kind === "slide") usedFolieIds.add(ref);
    else if (kind === "gap" && ref !== "start") usedFolieIds.add(ref);
  });

  const unused = folien.filter((f) => !usedFolieIds.has(f.id));
  if (unused.length === 0) return;

  for (const f of unused) {
    if (f.blobPath) await deleteBlob(f.blobPath);
    await deleteEntity(sitzungId, f.id);
  }

  const keptCount = folien.length - unused.length;
  if (keptCount === 0) {
    await deleteEntity(sitzungId, "meta");
  } else {
    await mergeEntity(sitzungId, "meta", { SeitenAnzahl: keptCount });
  }
};

// Lädt eine PDF-Präsentation hoch: zerlegt sie in Einzelseiten, lädt jede als
// Mid-Resolution-JPEG in den Blob-Container und schreibt Meta + Folienzeilen.
// Ersetzt eine bereits vorhandene Präsentation derselben Sitzung vollständig
// (Re-Upload statt separatem "Ersetzen"-Fluss).
// onProgress(current, total) wird nach jeder verarbeiteten Seite aufgerufen.
export const uploadPraesentation = async (
  { projectUuid, sitzungId, file, hochgeladenVon },
  onProgress
) => {
  if (!sitzungId) throw new Error("Keine laufende Sitzung.");
  if (!file || file.type !== "application/pdf") {
    throw new Error("Bitte eine PDF-Datei auswählen.");
  }

  await ensureTableExists(TABLE_NAME);
  await ensureContainerExists();

  // Vorherige Präsentation dieser Sitzung entfernen (Blobs + Zeilen), damit
  // kein Datenmüll aus einem Re-Upload zurückbleibt.
  await deletePraesentation(projectUuid, sitzungId);

  const buffer = await file.arrayBuffer();
  const pdfDoc = await pdfjsLib.getDocument({ data: buffer }).promise;
  const numPages = pdfDoc.numPages;
  const now = new Date().toISOString();

  for (let seite = 1; seite <= numPages; seite += 1) {
    const blob = await renderPageToJpegBlob(pdfDoc, seite);
    const blobPath = `${sitzungId}/${padSeite(seite)}.jpg`;
    await uploadBlob(blobPath, blob);
    await insertEntity({
      PartitionKey: sitzungId,
      RowKey: folieRowKey(seite),
      Seite: seite,
      BlobPath: blobPath,
      ErstelltAm: now,
    });
    onProgress?.(seite, numPages);
  }

  await insertEntity({
    PartitionKey: sitzungId,
    RowKey: "meta",
    Dateiname: cleanValue(file.name),
    SeitenAnzahl: numPages,
    HochgeladenAm: now,
    HochgeladenVon: cleanValue(hochgeladenVon),
  });

  return getPraesentation(sitzungId);
};
